// Dojo widget that hosts the eGo email editor when it's driven by the evisio2 AI bridge.
//
// Mounts emailForm DIRECTLY (not emailFormStandalone). Standalone was racing: its
// loadFormByID calls bodyHtmlEditor.destroy() + SCEditor_reload() while HtmlEditor's
// own postCreate setTimeout was still pending → two sceditor.create() calls on the
// same textarea → two visible iframes. The mirror path in AddressView (AddressView.ts:379)
// goes straight to emailForm + .show("dlg") and works fine; we follow that pattern but
// with our own GenericDialog so the wrapper widget hosts the form inside formContainer.
//
// Singleton enforces one-session-at-a-time. Publishes BRIDGE_TOPIC.EmailEditorOpened
// after mount (wiring registers AI tools) and BRIDGE_TOPIC.EmailEditorClosed on dialog
// close (tools unregister).
//
// Zero-mod policy: the email editor widget tree (emailForm.ts, emailFormStandalone.ts)
// is read/written exclusively via existing public APIs (getData, setSubject, addContent,
// addRecipients, setup) and via private-field reach (`(mailForm as any).inputTO.getValue()`,
// etc.) where no public surface exists.

import topic from 'dojo/topic';
import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './evisioEmailEditor.html?raw';
import _WidgetBaseSP from 'more/_WidgetBaseSP';
import RequestManager from '../../../more/RequestManager';
import GenericDialog from '../../../moreCore/UI/GenericDialog/GenericDialog';
import emailForm from '../../EMail/emailForm';
import {
    BRIDGE_TOPIC,
    EmailAttachmentInfo,
    EvisioBridgeErrorShape,
    GetEmailAttachmentResult,
    GetEmailDraftResult,
    ListEmailAttachmentsResult,
    OpenEmailEditorPayload,
} from "../bridge/EvisioBridgeTypes";

const SINGLETON_KEY = "__evisioEmailEditor";

interface DescriptorWithBase64 {
    filename?: string;
    base64Content?: string;
    extension?: string;
}


// Module-level singleton accessor — @declare drops `static` class members at runtime,
// so the instance() lookup lives here instead of as a static method (see auto-memory
// project_dojo_declare_no_statics).
function getInstance(): EvisioEmailEditor {
    let existing: any = (window as any)[SINGLETON_KEY];
    // Skip stale widgets — destroyRecursive sets _destroyed; a torn-down singleton
    // would crash on attach-point access on the next open.
    if (existing && !existing._destroyed) return existing;
    let inst: any = new (EvisioEmailEditor as any)();
    (window as any)[SINGLETON_KEY] = inst;
    return inst;
}
class EvisioEmailEditor extends compose(_WidgetBaseSP) {
    templateString: any = template;

    // Attach-point — matches data-dojo-attach-point="formContainer" in the .html.
    // emailForm gets placeAt'd here once the dialog is open.
    formContainer: HTMLDivElement;

    private _current: MdsLax<InstanceType<typeof emailForm>> | null = null;
    private _dlg: MdsLax<InstanceType<typeof GenericDialog>> | null = null;

    postCreate(): void {
        let self: any = this;
        super.postCreate.apply(this, arguments as any);
    }

    startup(): void {
        let self: any = this;
        super.startup.apply(this, arguments as any);
    }

    public isOpen(): boolean {
        return this._current != null;
    }

    // Bridge client calls into here after role gate + context validation + body
    // normalization. We own the dialog, the singleton slot, and the lifecycle pub.
    // Dialog shows the widget (this); after the dialog is up, emailForm gets placed
    // into formContainer. Pattern mirrors AddressView.ts:379-402 — direct emailForm
    // construction with explicit setup() + prefill, no reloadEditor / no standalone.
    public async open(payload: OpenEmailEditorPayload, context: any, body: string | undefined): Promise<{ opened: true }> {
        if (this._current != null){
            throw { kind: "editorAlreadyOpen", message: "the email editor is already open — close the existing dialog before opening another" } as EvisioBridgeErrorShape;
        }

        // Show this widget in our own GenericDialog. GenericDialog.show accepts a
        // widget directly (GenericDialog.ts:686).
        let dlg = new GenericDialog();
        dlg.destroyOnClose = false;
        dlg.closeOnEnter = false;
        dlg.closeOnEscape = false;

        let title = ((window as any).translations && (window as any).translations["email_mailFormStandalone_CreateNew"]) || "Compose email";
        dlg.show(
            this,
            title,
            "",
            "",
            "close",
            () => this._onClose(),
            { size: "larger" }
        );

        this._dlg = dlg;

        // Construct + setup emailForm directly. setup(null, ctx) builds a blank draft
        // and stamps _loadingWasInitialized=true so a later auto-startup() doesn't
        // re-run setup. placeAt into our attach-point. NO reloadEditor — that's the
        // race that caused two sceditors when emailFormStandalone was in the mix.
        let mf = new emailForm();
        mf.setup(null, context);
        mf.placeAt(this.formContainer);

        // Manual prefill — replicates what emailFormStandalone.prefill / _applyPrefill
        // would have done. addContent on a blank body == set; sceditor reads the
        // textarea value when it initializes via HtmlEditor.postCreate's 100ms timer.
        if (payload && payload.subject) mf.setSubject(payload.subject);
        if (typeof body === "string" && body.length) mf.addContent(body);
        let toMailRecipients = (mails: string[] | undefined) => {
            if (!Array.isArray(mails) || !mails.length) return null;
            return mails.map(m => ({ mailaddress: m, entityID: null as string | null }));
        };
        let toList = toMailRecipients(payload && payload.to);
        let ccList = toMailRecipients(payload && payload.cc);
        let bccList = toMailRecipients(payload && payload.bcc);
        if (toList) mf.addRecipients("to", toList);
        if (ccList) mf.addRecipients("cc", ccList);
        if (bccList) mf.addRecipients("bcc", bccList);

        this._current = mf;
        topic.publish(BRIDGE_TOPIC.EmailEditorOpened, {});

        return { opened: true };
    }

    public getDraft(): GetEmailDraftResult {
        let mf = this._getMailFormOrThrow();
        let data = (typeof mf.getData === "function") ? mf.getData() : null;

        // bodyHtmlEditor's sceditor state may lag formData.body — call updateContentData
        // first so the read matches what the user sees in the editor right now.
        let bodyHtml = "";
        let editor = mf.bodyHtmlEditor;
        if (editor){
            try { if (typeof editor.updateContentData === "function") editor.updateContentData(); } catch { /* sceditor not yet up */ }
            if (typeof editor.getHtmlContent === "function") bodyHtml = editor.getHtmlContent() || "";
        }
        if (!bodyHtml && data && typeof data.body === "string") bodyHtml = data.body;

        let subject = "";
        if (mf.inputSubject && typeof mf.inputSubject.getValue === "function") subject = mf.inputSubject.getValue() || "";
        if (!subject && data && typeof data.subject === "string") subject = data.subject;

        return {
            subject: subject,
            bodyHtml: bodyHtml,
            to: this._extractMail(mf.inputTO),
            cc: this._extractMail(mf.inputCC),
            bcc: this._extractMail(mf.inputBCC),
        };
    }

    public setDraft(partial: { subject?: string; bodyHtml?: string } | undefined | null): { ok: true } {
        let mf = this._getMailFormOrThrow();
        if (!partial || typeof partial !== "object"){
            throw { kind: "invalidParams", message: "setEmailDraft payload must be an object with subject and/or bodyHtml" } as EvisioBridgeErrorShape;
        }
        let keys = Object.keys(partial);
        for (let k of keys){
            if (k !== "subject" && k !== "bodyHtml"){
                throw { kind: "invalidParams", message: "setEmailDraft: unknown field '" + k + "'. Recipients (to/cc/bcc) are read-only here — the user edits those manually." } as EvisioBridgeErrorShape;
            }
        }
        if (typeof partial.subject === "string" && typeof mf.setSubject === "function"){
            mf.setSubject(partial.subject); // existing public method on emailForm
        }
        let bodyChanged = false;
        if (typeof partial.bodyHtml === "string" && mf.bodyHtmlEditor && typeof mf.bodyHtmlEditor.setHtmlContent === "function"){
            mf.bodyHtmlEditor.setHtmlContent(partial.bodyHtml);
            bodyChanged = true;
        }
        if (bodyChanged) this._cleanupMultipleEditorInstances();
        return { ok: true };
    }

    public appendBody(payload: { html?: string } | undefined | null): { ok: true } {
        let mf = this._getMailFormOrThrow();
        if (!payload || typeof payload.html !== "string"){
            throw { kind: "invalidParams", message: "appendEmailBody requires { html: string }" } as EvisioBridgeErrorShape;
        }
        if (typeof mf.addContent === "function"){
            mf.addContent(payload.html); // existing public method
        } else if (mf.bodyHtmlEditor && typeof mf.bodyHtmlEditor.addHtmlContent === "function"){
            mf.bodyHtmlEditor.addHtmlContent(payload.html);
        }
        this._cleanupMultipleEditorInstances();
        return { ok: true };
    }

    // sceditor's multi-instance rat tail — defensive sweep after body writes. With
    // standalone gone, the open-time race no longer triggers, but setHtmlContent/
    // addHtmlContent on bodyHtmlEditor can still spawn duplicates under some timings.
    // Helper lives on HtmlEditor (HtmlEditor.ts:891). Null-safe.
    private _cleanupMultipleEditorInstances(): void {
        let editor = this._current && (this._current as any).bodyHtmlEditor;
        if (!editor || typeof editor.helper_preventMultipleEditorInstancesCleanup !== "function") return;
        try { editor.helper_preventMultipleEditorInstancesCleanup(); }
        catch (e){ /* swallow — cleanup must not break the write */ }
    }

    public listAttachments(): ListEmailAttachmentsResult {
        let mf = this._getMailFormOrThrow();
        let entries = this._currentAttachments(mf);
        let out: EmailAttachmentInfo[] = [];
        for (let e of entries){
            if (!e || !e.guid) continue;
            out.push({ id: e.guid, filename: e.filename || "", mime: this._guessMimeFromFilename(e.filename || "") });
        }
        return { attachments: out };
    }

    public async getAttachmentBytes(payload: { id?: string } | undefined | null): Promise<GetEmailAttachmentResult> {
        let mf = this._getMailFormOrThrow();
        if (!payload || typeof payload.id !== "string" || !payload.id){
            throw { kind: "invalidParams", message: "getEmailAttachment requires { id: string }" } as EvisioBridgeErrorShape;
        }
        let id = payload.id;

        // Verify the id belongs to the current draft — otherwise the agent could request
        // any descriptor by guessing GUIDs.
        let entries = this._currentAttachments(mf);
        let entry = entries.find(e => e && e.guid === id);
        if (!entry){
            throw { kind: "attachmentNotFound", message: "no attachment with id '" + id + "' on the current draft" } as EvisioBridgeErrorShape;
        }

        // Raw attachments (pasted/dragged inline) carry their base64 in-memory keyed
        // by the same guid the store uses. Skip the backend round-trip when local.
        let raws: Array<{ fileID: string; base64: string }> = Array.isArray(mf.rawFileAttachments) ? mf.rawFileAttachments : [];
        let raw = raws.find(r => r && r.fileID === id);
        if (raw){
            return {
                name: entry.filename || "",
                mime: this._guessMimeFromFilename(entry.filename || ""),
                base64: this._stripDataUriPrefix(raw.base64 || ""),
            };
        }

        // Regular attachment — pull base64 via descriptor backend. Guid IS the download
        // key per filePreview.ts:236.
        let descriptor = await this._downloadDescriptor(id);
        if (!descriptor || typeof descriptor.base64Content !== "string"){
            throw { kind: "attachmentNotFound", message: "backend returned no content for attachment '" + id + "'" } as EvisioBridgeErrorShape;
        }
        let cleanBase64 = this._stripDataUriPrefix(descriptor.base64Content);
        let name = descriptor.filename || entry.filename || "";
        let mime = this._extractDataUriMime(descriptor.base64Content) || this._guessMimeFromFilename(name);
        return { name: name, mime: mime, base64: cleanBase64 };
    }

    // _current IS the emailForm now (used to be emailFormStandalone wrapping mailForm).
    private _getMailFormOrThrow(): any {
        if (!this._current){
            throw { kind: "editorNotOpen", message: "no email editor is open — call openEmailEditor first" } as EvisioBridgeErrorShape;
        }
        return this._current;
    }

    private _currentAttachments(mf: any): Array<{ guid: string; filename: string }> {
        let store = mf && mf.attachmentsStore;
        if (!store || typeof store.getCurrentCompleteList !== "function") return [];
        let list = store.getCurrentCompleteList();
        return Array.isArray(list) ? list : [];
    }

    private _extractMail(input: any): string[] {
        if (!input || typeof input.getValue !== "function") return [];
        let v = input.getValue();
        if (!Array.isArray(v)) return [];
        let out: string[] = [];
        for (let r of v){ if (r && typeof r.mailaddress === "string" && r.mailaddress) out.push(r.mailaddress); }
        return out;
    }

    private _onClose(): void {
        this._teardown();
        topic.publish(BRIDGE_TOPIC.EmailEditorClosed, {});
    }

    private _teardown(): void {
        if (this._current){
            try { if (typeof this._current.destroyRecursive === "function") this._current.destroyRecursive(); } catch { /* ignore */ }
            this._current = null;
        }
        if (this._dlg){
            try { if (typeof (this._dlg as any).destroy === "function") (this._dlg as any).destroy(); } catch { /* ignore */ }
            this._dlg = null;
        }
        // Destroy the wrapper widget itself so its DOM + attach-points unwind cleanly.
        // Clear the singleton slot so the next instance() call constructs a fresh widget.
        let self: any = this;
        if (typeof self.destroyRecursive === "function" && !self._destroyed){
            try { self.destroyRecursive(); } catch { /* ignore */ }
        }
        try { delete (window as any)[SINGLETON_KEY]; } catch { (window as any)[SINGLETON_KEY] = undefined; }
    }

    private _downloadDescriptor(key: string): Promise<DescriptorWithBase64> {
        return new Promise<DescriptorWithBase64>((resolve, reject) => {
            let req = new RequestManager();
            req.callfunction(
                "Webtool_API_FileTransfer__DownloadDescriptorByKey",
                { key: key },
                (res: DescriptorWithBase64) => resolve(res),
                (err: any) => reject({ kind: "backendError", message: "descriptor fetch failed: " + ((err && err.Message) || String(err)) } as EvisioBridgeErrorShape),
                false
            );
        });
    }

    private _stripDataUriPrefix(b64: string): string {
        if (typeof b64 !== "string") return "";
        let m = /^data:[^;,]+(?:;[^,]*)?,/.exec(b64);
        return m ? b64.substring(m[0].length) : b64;
    }

    private _extractDataUriMime(b64: string): string | null {
        if (typeof b64 !== "string") return null;
        let m = /^data:([^;,]+)(?:;[^,]*)?,/.exec(b64);
        return m ? m[1] : null;
    }

    private _guessMimeFromFilename(name: string): string {
        if (!name) return "application/octet-stream";
        let dot = name.lastIndexOf(".");
        if (dot < 0) return "application/octet-stream";
        let ext = name.substring(dot + 1).toLowerCase();
        switch (ext){
            case "pdf":  return "application/pdf";
            case "txt":  return "text/plain";
            case "csv":  return "text/csv";
            case "json": return "application/json";
            case "xml":  return "application/xml";
            case "html":
            case "htm":  return "text/html";
            case "png":  return "image/png";
            case "jpg":
            case "jpeg": return "image/jpeg";
            case "gif":  return "image/gif";
            case "webp": return "image/webp";
            case "svg":  return "image/svg+xml";
            case "doc":  return "application/msword";
            case "docx": return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
            case "xls":  return "application/vnd.ms-excel";
            case "xlsx": return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            case "ppt":  return "application/vnd.ms-powerpoint";
            case "pptx": return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
            case "zip":  return "application/zip";
            case "eml":  return "message/rfc822";
            case "msg":  return "application/vnd.ms-outlook";
            default:     return "application/octet-stream";
        }
    }
}

// Attach the singleton accessor to the post-declare constructor so external callers
// can do EvisioEmailEditor.instance() — class-level statics don't survive @declare
// (see project_dojo_declare_no_statics memory).
(EvisioEmailEditor as any).instance = getInstance;

declare namespace EvisioEmailEditor {
    function instance(): EvisioEmailEditor;
}

export default mdsWidget(EvisioEmailEditor);
